"use client";

import { useState, useEffect } from "react";
import DailyInput from "@/components/DailyInput";
import DayScore from "@/components/DayScore";
import WeekView from "@/components/WeekView";
import Login from "@/components/Login";
import Onboarding from "@/components/Onboarding";
import Logo from "@/components/Logo";
import History from "@/components/History";
import ProfileScreen from "@/components/ProfileScreen";
import Invite from "@/components/Invite";
import { DailyLog, AIAnalysis, UserProfile, UserBasic } from "@/types";
import { saveLog, isUserLoggedIn, logoutUser, saveUserProfile, fetchLogs, fetchUserProfile, fetchCurrentUser, getUserInfo, getToken, fetchAICoachAnalysis, deleteUserProfile } from "@/utils/storage";
import { getTodayISO } from "@/utils/date";
import clsx from "clsx";
import { LogOut, Activity, Calendar, History as HistoryIcon, PlusCircle, User, Share2, Trophy, Mic } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";



import AchievementsScreen from "@/components/AchievementsScreen";

import VoiceParser from "@/components/VoiceParser";

export default function Home() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [view, setView] = useState<"log" | "today" | "week" | "history" | "profile" | "invite" | "achievements">("log");
  const [showVoiceParser, setShowVoiceParser] = useState(false);
  const [currentLog, setCurrentLog] = useState<DailyLog | null>(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [analysis, setAnalysis] = useState<AIAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [allLogs, setAllLogs] = useState<DailyLog[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [currentUser, setCurrentUser] = useState<UserBasic | null>(getUserInfo());

  const syncData = async () => {
    try {
      setIsLoggedIn(isUserLoggedIn());
      if (!isUserLoggedIn()) return; // Stop if not logged in

      const profile = await fetchUserProfile();
      const user = await fetchCurrentUser();
      setCurrentUser(user);

      // Update profile state
      setHasProfile(!!profile);
      setUserProfile(profile);

      // Fetch Logs
      const logs = await fetchLogs();
      const sortedLogs = [...logs].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setAllLogs(sortedLogs);

      // Check for today's log
      const todayStr = getTodayISO();
      const todayLog = sortedLogs.find(l => l.date === todayStr);

      // Verify Token
      if (!getToken()) {
        setIsLoggedIn(false);
        return;
      }

      if (todayLog) {
        setCurrentLog(todayLog);
        if (todayLog.aiAnalysis) {
          setAnalysis(todayLog.aiAnalysis);
        } else if (profile) {
          // Only auto-fetch AI on initial load or explict sync if missing
          setLoading(true);
          fetchAICoachAnalysis('daily', profile, { log: todayLog })
            .then(async (data) => {
              setAnalysis(data);
              const updatedLog = { ...todayLog, aiAnalysis: data };
              await saveLog(updatedLog);
              setCurrentLog(updatedLog);
              // Update local logs list slightly to reflect the new analysis
              setAllLogs(prev => {
                const others = prev.filter(l => l.date !== todayLog.date);
                return [...others, updatedLog].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
              });
            })
            .catch(err => console.error("AI fetch failed during sync", err))
            .finally(() => setLoading(false));
        }
      } else {
        // Reset current log if today has no log (e.g. new day started)
        if (view !== 'history') { // Keep current log if viewing history? accessing via state...
          // Actually if there is no log for today, and we are in 'today' view, it handles it.
          // But currentLog state acts as the "buffer" for the input form too?
          // No, DailyInput uses currentLog prop for initial data. 
        }
      }

    } catch (error) {
      console.error("Sync failed:", error);
    }
  };

  useEffect(() => {
    syncData();
  }, []);

  const handleLoginSuccess = async () => {
    await syncData();
    setIsLoggedIn(true); // syncData sets this but let's be sure
  };

  const handleProfileComplete = (profile: UserProfile) => {
    saveUserProfile(profile);
    setHasProfile(true);
    setUserProfile(profile);
  };

  const handleLogout = () => {
    logoutUser();
    setIsLoggedIn(false);
    setHasProfile(false);
    setView("log");
    setCurrentLog(null);
    setAnalysis(null);
  };

  const handleSaveDay = async (log: DailyLog) => {
    // 1. Initial save (optimistic/safety)
    setCurrentLog(log);
    await saveLog(log);

    // Update list view from LOCAL storage immediately to avoid race conditions with backend
    // fetchLogs() logic...
    setAllLogs(prev => {
      const others = prev.filter(l => l.date !== log.date);
      const newLogs = [...others, log].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      return newLogs;
    });

    setLoading(true);
    setView("today");

    try {
      if (!userProfile) {
        alert("Please complete your profile to get AI analysis.");
        setView("profile");
        return;
      }

      // 2. Fetch AI Analysis
      const data = await fetchAICoachAnalysis('daily', userProfile, { log });
      setAnalysis(data);

      // 3. Persist AI Analysis to the log
      const updatedLog = { ...log, aiAnalysis: data };
      await saveLog(updatedLog);
      setCurrentLog(updatedLog); // Update current log state

      // Update lists again
      setAllLogs(prev => {
        const others = prev.filter(l => l.date !== log.date);
        const newLogs = [...others, updatedLog].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        return newLogs;
      });

    } catch (error) {
      console.error(error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      alert(`Failed to analyze: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };


  const handleUpdateProfile = (updatedProfile: UserProfile) => {
    saveUserProfile(updatedProfile);
    setUserProfile(updatedProfile);
    setView("log"); // Return to log after save
    syncData(); // Re-sync to reflect any profile changes if needed
  };

  const handleDeleteProfile = async () => {
    await deleteUserProfile();
    // Total reset and logout
    setIsLoggedIn(false);
    setCurrentUser(null);
    setHasProfile(false);
    setUserProfile(null);
    setAllLogs([]);
    setAnalysis(null);
    setCurrentLog(null);
    
    setView("log");
    alert("Account and data permanently deleted.");
  };

  // --- Auto Refresh Logic ---
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && isLoggedIn) {
        console.log("App foregrounded - refreshing data...");
        syncData();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isLoggedIn]); // Re-bind if login state changes, effectively active when logged in

  // --- Pull to Refresh Logic ---
  const [pullY, setPullY] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [touchStart, setTouchStart] = useState(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (window.scrollY === 0 && !isRefreshing) {
      setTouchStart(e.touches[0].clientY);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStart > 0 && !isRefreshing) {
      const y = e.touches[0].clientY;
      const diff = y - touchStart;
      if (diff > 0) {
        setPullY(diff * 0.4); // Resistance
      }
    }
  };

  const handleTouchEnd = () => {
    if (pullY > 80 && !isRefreshing) {
      setIsRefreshing(true);
      setPullY(80); // Snap to threshold
      syncData().finally(() => {
        setTimeout(() => {
          setIsRefreshing(false);
          setPullY(0);
        }, 500); // Small delay for visual feel
      });
    } else {
      setPullY(0);
      setTouchStart(0);
    }
  };

  if (!isLoggedIn) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  if (!hasProfile) {
    return <Onboarding onComplete={handleProfileComplete} />;
  }

  return (
    <div className="flex-1 flex flex-col min-h-[100dvh] bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-[#020617] to-[#020617]">
      {/* Top Bar */}
      <header className="flex items-center justify-between px-6 py-5 glass border-b border-white/5 z-[60] relative">
        <div className="flex items-center gap-3">
          <Logo size="sm" showText={false} />
          <div className="flex flex-col">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-white">
              {userProfile?.name ? userProfile.name : "LongevityOS v1.1"}
            </span>
            <span className="text-[10px] text-text-muted font-medium">
              {userProfile?.goal || "Your Fitness Journey"}
            </span>
          </div>
        </div>

        {/* Pull to Refresh Indicator */}
        {(pullY > 0 || isRefreshing) && (
          <div
            className="absolute top-20 left-1/2 -translate-x-1/2 z-50 flex items-center justify-center p-2 rounded-full glass border border-white/10 shadow-lg"
            style={{ transform: `translate(-50%, ${Math.min(pullY, 80) * 0.5}px)` }}
          >
            <div className={clsx("w-5 h-5 border-2 border-primary border-t-transparent rounded-full", isRefreshing ? "animate-spin" : "")} />
          </div>
        )}

        <div className="flex items-center gap-3">



          <div className="relative">
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-text-muted hover:text-white hover:bg-white/10 transition-colors relative"
            >
              <User size={18} />
            </button>

            <AnimatePresence>
              {showProfileMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowProfileMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 top-12 w-48 bg-[#020617] border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden z-[100] flex flex-col p-1"
                  >
                    <button
                      onClick={() => {
                        setView("profile");
                        setShowProfileMenu(false);
                      }}
                      className="flex items-center gap-3 w-full p-3 hover:bg-white/5 rounded-xl text-left transition-colors text-sm font-medium text-white"
                    >
                      <User size={16} className="text-primary" />
                      My Profile
                    </button>
                    <div className="h-[1px] bg-white/5 mx-2 my-1" />
                    <button
                      onClick={() => {
                        handleLogout();
                        setShowProfileMenu(false);
                      }}
                      className="flex items-center gap-3 w-full p-3 hover:bg-red-500/10 hover:text-red-400 rounded-xl text-left transition-colors text-sm font-medium text-text-muted"
                    >
                      <LogOut size={16} />
                      Log Out
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          <button
            onClick={() => setView("achievements")}
            className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <Trophy size={18} className={view === "achievements" ? "text-yellow-500" : ""} />
          </button>

          <button
            onClick={async () => {
              try {
                if (navigator.share) {
                  await navigator.share({
                    title: "LongevityOS",
                    text: "Check out LongevityOS - Your personal AI health & longevity coach.",
                    url: window.location.href,
                  });
                } else {
                  await navigator.clipboard.writeText(window.location.href);
                  alert("Link copied to clipboard!");
                }
              } catch (err) {
                console.error("Error sharing:", err);
              }
            }}
            className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-text-muted hover:text-white hover:bg-white/10 transition-colors"
          >
            <Share2 size={18} />
          </button>
        </div>
      </header>

      {/* Content Area */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden relative z-0">
        <AnimatePresence mode="wait">
          {["log", "today", "week", "history"].includes(view) ? (
            <motion.div
              key="pull-view-wrapper"
              className="h-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}

              // Touch Handlers for Pull to Refresh
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >

              <div className="p-4 pb-40 min-h-full">
                <motion.div
                  key={view}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="max-w-md mx-auto h-full"
                >
                  {view === "log" && (
                    <DailyInput
                      onSave={handleSaveDay}
                      currentLog={currentLog || null}
                    />
                  )}
                  {view === "week" && (
                    <WeekView logs={allLogs} profile={userProfile || undefined} />
                  )}
                  {view === "history" && (
                    <History />
                  )}
                  {view === "today" && (() => {
                    const todayStr = getTodayISO();
                    const todaysLog = allLogs.find(l => l.date === todayStr) || currentLog;

                    if (todaysLog) {
                      return <DayScore log={todaysLog} analysis={analysis} loading={loading} />;
                    } else {
                      return (
                        <div className="flex flex-col items-center justify-center h-64 text-text-muted">
                          <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                            <Activity size={24} className="text-text-muted" />
                          </div>
                          <p className="text-xs font-medium">No data for today.</p>
                          <button
                            onClick={() => setView('log')}
                            className="text-[10px] mt-2 text-primary hover:underline"
                          >
                            Log your day
                          </button>
                        </div>
                      );
                    }
                  })()}
                </motion.div>
              </div>

            </motion.div>
          ) : (
            <motion.div
              key="fullscreen-view-wrapper"
              className="h-full p-4 pb-24 overflow-y-auto overflow-x-hidden"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <div className="h-full max-w-md mx-auto">
                {view === "achievements" && userProfile && (
                  <AchievementsScreen
                    logs={allLogs}
                    profile={userProfile}
                    onBack={() => setView("log")}
                  />
                )}
                {view === "profile" && userProfile && (
                  <ProfileScreen
                    currentProfile={userProfile}
                    onSave={handleUpdateProfile}
                    onBack={() => setView("log")}
                    onDelete={handleDeleteProfile}
                  />
                )}
                {view === "invite" && (
                  <Invite onBack={() => setView("log")} />
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Bottom Navigation */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[calc(100%-48px)] max-w-sm z-50">
        <nav className="glass rounded-2xl p-1.5 flex justify-between items-center shadow-2xl shadow-black/50 border border-white/10">
          {[
            { id: "log", icon: PlusCircle, label: "Log" },
            { id: "today", icon: Activity, label: "Today" },
            { id: "week", icon: Calendar, label: "Week" },
            { id: "history", icon: HistoryIcon, label: "History" },
            ...(currentUser && (currentUser.role === "superadmin" || currentUser.role === "admin")
              ? [{ id: "invite", icon: User, label: "Invite" }]
              : []),
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setView(item.id as "log" | "today" | "week" | "history" | "profile" | "invite")}
              className={clsx(
                "flex-1 flex flex-col items-center gap-1 py-2 rounded-xl transition-all duration-300 relative overflow-hidden",
                view === item.id
                  ? "text-primary"
                  : "text-text-muted hover:text-white hover:bg-white/5"
              )}
            >
              {view === item.id && (
                <motion.div
                  layoutId="nav-bg"
                  className="absolute inset-0 bg-primary/10 rounded-xl"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                />
              )}
              <item.icon size={18} strokeWidth={view === item.id ? 2.5 : 2} className="relative z-10" />
              <span className="text-[10px] font-medium relative z-10">{item.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Voice Parser Modal */}
      <AnimatePresence>
        {showVoiceParser && (
          <VoiceParser
            onCancel={() => setShowVoiceParser(false)}
            placeholder="I ate eggs for breakfast, ran 5k, slept 8 hours, and felt great."
            onSave={(parsedData) => {
              // Merge with current log
              const todayStr = getTodayISO();
              // If we have a current log, merge. If not, create new base.
              // Note: currentLog state might be null if day not started, so use defensive merging

              const baseLog = currentLog || {
                date: todayStr,
                meals: [
                  { name: "Breakfast", items: [] },
                  { name: "Lunch", items: [] },
                  { name: "Dinner", items: [] },
                  { name: "Snacks", items: [] },
                ],
                workouts: [{ type: "Rest", duration: 0, calories: 0 }],
                steps: 0,
                sleep: 0,
                water: 0,
                supplements: {},
                mood: "",
                alcohol: { type: "", units: 0 },
                smoking: { type: "No", quantity: 0 },
                stress: 0,
                meditation: 0,
                sunlight: 0,
                screenTime: 0,
                screenFreeBeforeBed: false,
                socialConnect: 0,
              };

              // 1. Meals - Push to existing? Or replace? 
              // The AI returns "meals" array. We should probably APPEND items to matching meal names if they exist, or just replace if complex.
              // The Parser returns a partial DailyLog.

              const newMeals = [...baseLog.meals];
              if ((parsedData as any).meals) {
                (parsedData as any).meals.forEach((parsedMeal: any) => {
                  const targetIndex = newMeals.findIndex(m => m.name.toLowerCase() === parsedMeal.name.toLowerCase());
                  if (targetIndex >= 0) {
                    newMeals[targetIndex].items = [...newMeals[targetIndex].items, ...parsedMeal.items];
                  }
                });
              }

              // Extract workouts and steps from voice parser result
              const newWorkouts = (parsedData as any).workout 
                ? [{
                    type: (parsedData as any).workout.type || "Rest",
                    duration: (parsedData as any).workout.duration || 0,
                    calories: (parsedData as any).workout.calories || 0
                  }]
                : baseLog.workouts;

              const newSteps = (parsedData as any).workout?.steps ?? (parsedData as any).steps ?? baseLog.steps;

              const mergedLog: DailyLog = {
                ...baseLog,
                ...parsedData, // Overwrite simple fields (sleep, water, etc)
                meals: newMeals, // Use our smart-merged meals
                workouts: newWorkouts,
                steps: newSteps,
                alcohol: parsedData.alcohol ? { ...baseLog.alcohol, ...parsedData.alcohol } : baseLog.alcohol,
                smoking: parsedData.smoking ? { ...baseLog.smoking, ...parsedData.smoking } : baseLog.smoking,
                supplements: { ...baseLog.supplements, ...parsedData.supplements }
              };

              // Fix type incompatibilities if any (e.g. Activity Levels mapping if parser gets it wrong, but parser schema should match)

              setCurrentLog(mergedLog);
              setView("log"); // Go to log view to verify
              setShowVoiceParser(false);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}



